/**
 * React Hook for WebSocket Integration
 * 
 * Provides easy access to WebSocket functionality in React components.
 * Handles connection lifecycle, message delivery, and real-time updates.
 * 
 * Requirements: 6.2, 6.3, 6.4, 6.5, 4.5
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import websocketService from '../services/websocketService';

/**
 * Hook for WebSocket connection management
 * @param {string} userId - User ID for authentication
 * @returns {Object} WebSocket state and methods
 */
export const useWebSocket = (userId) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const unsubscribeRefs = useRef([]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    // Connect to WebSocket
    websocketService.connect(userId);

    // Subscribe to connection changes
    const unsubConnection = websocketService.onConnectionChange((connected) => {
      setIsConnected(connected);
      if (connected) {
        setError(null);
      }
    });

    // Subscribe to errors
    const unsubError = websocketService.onError((err) => {
      setError(err);
    });

    unsubscribeRefs.current = [unsubConnection, unsubError];

    // Cleanup on unmount
    return () => {
      unsubscribeRefs.current.forEach(unsub => unsub());
      websocketService.disconnect();
    };
  }, [userId]);

  return {
    isConnected,
    error,
    sendMessage: websocketService.sendMessage.bind(websocketService),
    sendTypingIndicator: websocketService.sendTypingIndicator.bind(websocketService),
    updateMessageStatus: websocketService.updateMessageStatus.bind(websocketService)
  };
};

/**
 * Hook for receiving real-time messages
 * @param {Function} onMessage - Callback for new messages
 */
export const useWebSocketMessages = (onMessage) => {
  useEffect(() => {
    if (!onMessage) {
      return;
    }

    const unsubscribe = websocketService.onMessage(onMessage);

    return () => {
      unsubscribe();
    };
  }, [onMessage]);
};

/**
 * Hook for typing indicators
 * @param {Function} onTyping - Callback for typing updates
 */
export const useTypingIndicator = (onTyping) => {
  useEffect(() => {
    if (!onTyping) {
      return;
    }

    const unsubscribe = websocketService.onTyping(onTyping);

    return () => {
      unsubscribe();
    };
  }, [onTyping]);
};

/**
 * Hook for presence updates
 * @param {Function} onPresence - Callback for presence updates
 */
export const usePresence = (onPresence) => {
  useEffect(() => {
    if (!onPresence) {
      return;
    }

    const unsubscribe = websocketService.onPresence(onPresence);

    return () => {
      unsubscribe();
    };
  }, [onPresence]);
};

/**
 * Hook for message status updates
 * @param {Function} onStatus - Callback for status updates
 */
export const useMessageStatus = (onStatus) => {
  useEffect(() => {
    if (!onStatus) {
      return;
    }

    const unsubscribe = websocketService.onStatus(onStatus);

    return () => {
      unsubscribe();
    };
  }, [onStatus]);
};

/**
 * Hook for managing typing indicator with debouncing
 * @param {string} threadId - Current thread ID
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 1000)
 * @returns {Function} Function to call when user is typing
 */
export const useTypingIndicatorSender = (threadId, debounceMs = 1000) => {
  const typingTimerRef = useRef(null);
  const isTypingRef = useRef(false);

  const sendTyping = useCallback(() => {
    if (!threadId) {
      return;
    }

    // Send typing indicator if not already sent
    if (!isTypingRef.current) {
      websocketService.sendTypingIndicator(threadId, true);
      isTypingRef.current = true;
    }

    // Clear existing timer
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }

    // Set timer to stop typing indicator
    typingTimerRef.current = setTimeout(() => {
      websocketService.sendTypingIndicator(threadId, false);
      isTypingRef.current = false;
    }, debounceMs);
  }, [threadId, debounceMs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
      if (isTypingRef.current && threadId) {
        websocketService.sendTypingIndicator(threadId, false);
      }
    };
  }, [threadId]);

  return sendTyping;
};

/**
 * Hook for tracking online users
 * @returns {Object} Online users state and methods
 */
export const useOnlineUsers = () => {
  const [onlineUsers, setOnlineUsers] = useState(new Set());

  useEffect(() => {
    const handlePresence = (data) => {
      setOnlineUsers(prev => {
        const updated = new Set(prev);
        if (data.isOnline) {
          updated.add(data.userId);
        } else {
          updated.delete(data.userId);
        }
        return updated;
      });
    };

    const unsubscribe = websocketService.onPresence(handlePresence);

    return () => {
      unsubscribe();
    };
  }, []);

  const isUserOnline = useCallback((userId) => {
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  return {
    onlineUsers: Array.from(onlineUsers),
    isUserOnline
  };
};

export default {
  useWebSocket,
  useWebSocketMessages,
  useTypingIndicator,
  usePresence,
  useMessageStatus,
  useTypingIndicatorSender,
  useOnlineUsers
};
